import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';

import withWidgetTestId from '@swf/core/hoc/withWidgetTestId';
import type { WidgetFC } from '@swf/core/types/Widget';
import { SportsComponent } from '@swf/sports/components';
import {
  OddsBoostEvents,
  PopularAccumulators,
  PopularBetBuilders,
  PopularSportsPicker
} from '@swf/sports/features';
import useMarketUpdates from '@swf/sports/hooks/useMarketUpdates';
import type { EventTypeId } from '@swf/sports/types/eventType';

import styles from './SportsHomePageWidget.scss';
import { InPlayEvents } from './components/InPlayEvents/InPlayEvents';
import { HomePageLoadingState } from './components/LoadingState/LoadingState';
import { UpcomingEvents } from './components/UpcomingEvents/UpcomingEvents';
import { sportsHomePageWidgetModel, useSportsHomePageWidget } from './model';

const WidgetContent = () => {
  const { widgetSelect, widgetDispatch } = useSportsHomePageWidget();
  const hasPopularBetBuilders = useSelector(widgetSelect.hasPopularBetBuilders);
  const popularBetBuilders = useSelector(widgetSelect.popularBetBuilders);
  const hasPopularSports = useSelector(widgetSelect.hasPopularSports);
  const popularSports = useSelector(widgetSelect.popularSports);
  const filteredPopularAccumulators = useSelector(widgetSelect.filteredPopularAccumulators);
  const hasPopularAccumulators = useSelector(widgetSelect.hasPopularAccumulators);
  const popularAccumulatorEventTypes = useSelector(widgetSelect.popularAccumulatorEventTypes);
  const selectedPopularAccumulatorsEventType = useSelector(
    widgetSelect.selectedPopularAccumulatorsEventType
  );
  const hasOddsBoostEvents = useSelector(widgetSelect.hasOddsBoostEvents);
  const oddsBoostEvents = useSelector(widgetSelect.oddsBoostEvents);
  const hasInPlayEvents = useSelector(widgetSelect.hasInPlayEvents);
  const hasUpcomingEvents = useSelector(widgetSelect.hasUpcomingEvents);

  const setSelectedEventType = useCallback(
    (eventTypeId: EventTypeId) => {
      widgetDispatch.setSelectedPopularAccumulatorsEventType(eventTypeId);
    },
    [widgetDispatch]
  );

  return (
    <div className={styles.wrapper}>
      {hasPopularSports ? <PopularSportsPicker popularSports={popularSports} /> : null}
      {hasOddsBoostEvents ? (
        <SportsComponent name="Sports Odds Boost Events Component">
          <OddsBoostEvents oddsBoostEvents={oddsBoostEvents} />
        </SportsComponent>
      ) : null}
      {hasPopularAccumulators ? (
        <PopularAccumulators
          popularAccumulators={filteredPopularAccumulators}
          tabs={popularAccumulatorEventTypes}
          selectedEventType={selectedPopularAccumulatorsEventType}
          setSelectedEventType={setSelectedEventType}
        />
      ) : null}
      {hasPopularBetBuilders ? (
        <PopularBetBuilders popularBetBuilders={popularBetBuilders} />
      ) : null}
      {hasInPlayEvents ? <InPlayEvents /> : null}
      {hasUpcomingEvents ? <UpcomingEvents /> : null}
    </div>
  );
};

const SportsHomePageWidget: WidgetFC = () => {
  const { widgetSelect, widgetDispatch } = useSportsHomePageWidget();
  const isLoading = useSelector(widgetSelect.isLoading);
  const hasPreloaded = useSelector(widgetSelect.hasPreloaded);
  const hasErrored = useSelector(widgetSelect.hasErrored);
  const marketIds = useSelector(widgetSelect.marketIds);

  useMarketUpdates(marketIds, widgetDispatch.marketsUpdated);

  if (isLoading && !hasPreloaded) {
    return <HomePageLoadingState />;
  }

  if (hasErrored) {
    return null;
  }

  return <WidgetContent />;
};

SportsHomePageWidget.model = sportsHomePageWidgetModel;

export default withWidgetTestId(SportsHomePageWidget, 'sports-home-page');
